import { Button } from 'components/common/Button'
import { Info } from 'components/common/Info'
import { Separator } from 'components/common/Separator'
import { Paragraph, Title } from 'components/common/ways-to/Typography'
import { WaysToProps } from 'components/common/ways-to/WaysTo'
import { Currencies } from 'constants/currencies'
import { useCurrencies } from 'contexts'
import { useEditionPrices } from 'hooks/useEditionPrices'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { SellingTradeType } from 'types'
import { getArtworkUrlFromRoot } from 'utils/urls'

export function WaysToSell({
  edition,
  artwork,
  artists,
}: Omit<WaysToProps, 'type'>) {
  const { t } = useTranslation('common')
  const { highestBid, lowestAsk } = useEditionPrices({ edition })
  const { formatCurrency } = useCurrencies()

  function getActionHref(action: SellingTradeType) {
    return `${getArtworkUrlFromRoot(
      'ask-or-sell',
      artists,
      artwork?.name,
      artwork?.id
    )}/${edition?.name}?action=${action}`
  }

  return (
    <div className='space-y-6'>
      {!highestBid && (
        <>
          {/* first way */}
          <div className='space-y-2.5'>
            <Title>{t('how_it_works_drawer.selling.no_offers.1.title')}</Title>
            <Info>
              <Trans
                i18nKey='common:how_it_works_drawer.selling.no_offers.1.info'
                components={{ b: <b /> }}
              />
            </Info>
            <Paragraph>
              {t('how_it_works_drawer.selling.no_offers.1.description')}
            </Paragraph>
          </div>

          <Separator color='cool-400' />

          {/* second way */}
          <div className='space-y-2.5'>
            <Title>{t('how_it_works_drawer.selling.no_offers.2.title')}</Title>

            <div className='space-y-6'>
              <Paragraph>
                <Trans
                  i18nKey='common:how_it_works_drawer.selling.no_offers.2.description_1'
                  components={{ b: <b /> }}
                />
              </Paragraph>
              <Paragraph>
                <Trans
                  i18nKey={
                    lowestAsk
                      ? 'common:how_it_works_drawer.selling.no_offers.2.description_2_with_listing'
                      : 'common:how_it_works_drawer.selling.no_offers.2.description_2_without_listing'
                  }
                  components={{ b: <b /> }}
                  values={{
                    lowestListing: formatCurrency(lowestAsk, {
                      from: Currencies.USER,
                      to: Currencies.USER,
                    }),
                  }}
                />
              </Paragraph>
              <Paragraph>
                {t('how_it_works_drawer.selling.no_offers.2.description_3')}
              </Paragraph>
            </div>

            <Button
              className='w-full'
              style='solid-black'
              pill
              type='link'
              href={getActionHref('ask')}
            >
              {t('how_it_works_drawer.selling.no_offers.2.action')}
            </Button>
          </div>
        </>
      )}

      {Boolean(highestBid) && (
        <>
          {/* first way */}
          <div className='space-y-2.5'>
            <Title>
              {t('how_it_works_drawer.selling.with_offers.1.title')}
            </Title>
            <Paragraph>
              <Trans
                i18nKey='common:how_it_works_drawer.selling.with_offers.1.description'
                components={{ b: <b /> }}
                values={{
                  highestOffer: formatCurrency(highestBid, {
                    from: Currencies.USER,
                    to: Currencies.USER,
                  }),
                }}
              />
            </Paragraph>
            <Button
              className='w-full'
              style='solid-black'
              pill
              type='link'
              href={getActionHref('sell')}
            >
              {t('how_it_works_drawer.selling.with_offers.1.action')}
            </Button>
          </div>

          <Separator color='cool-400' />

          {/* second way */}
          <div className='space-y-2.5'>
            <Title>
              {t('how_it_works_drawer.selling.with_offers.2.title')}
            </Title>

            <div className='space-y-6'>
              <Paragraph>
                <Trans
                  i18nKey='common:how_it_works_drawer.selling.with_offers.2.description_1'
                  components={{ b: <b /> }}
                />
              </Paragraph>
              <Paragraph>
                <Trans
                  i18nKey={
                    lowestAsk
                      ? 'common:how_it_works_drawer.selling.with_offers.2.description_2_with_listing'
                      : 'common:how_it_works_drawer.selling.with_offers.2.description_2_without_listing'
                  }
                  components={{ b: <b /> }}
                  values={{
                    lowestListing: formatCurrency(lowestAsk, {
                      from: Currencies.USER,
                      to: Currencies.USER,
                    }),
                  }}
                />
              </Paragraph>
              <Paragraph>
                {t('how_it_works_drawer.selling.no_offers.2.description_3')}
              </Paragraph>
            </div>

            <Button
              className='w-full'
              style='outline'
              pill
              type='link'
              href={getActionHref('ask')}
            >
              {t('how_it_works_drawer.selling.with_offers.2.action')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
